import React, { useState, useMemo, useRef, useEffect, memo } from 'react';
import dayjs from 'dayjs';
import { PredictedDemandModel } from '../../../../app/services/api/podClient';
import { useTranslation } from 'react-i18next';
import { GroupedTableVirtuoso, TableComponents } from 'react-virtuoso';
import {
  Table, TableRow, TableCell, TextField, Box, Typography,
  useTheme, Checkbox, Tooltip, TableSortLabel, SvgIcon, Chip, CircularProgress,
} from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { ChevronRight } from '../../../../assets';
import { BulkOrderItem, BulkOrderItemModifier } from '../../../../app/services/api/orderManagementClient';
import { DemandModifierType } from '../../DemandModifiers/types/DemandModifierTypes';
import { getInventorySku } from '../../../../utils';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface BulkOrderTableProps {
	items: BulkOrderItem[];
  onOverrideChange: (storeNumber: string, itemId: number, itemSku: string, overrideQty: number | null) => Promise<void>;
  selectedStores: Set<string>;
	onStoreGroupSelection: (storeNumber: string, checked: boolean) => void;
	onPromoClick: (storeNumber: string, itemId: number, modifierId?: number) => void;
	predictedDemands?: Record<string, PredictedDemandModel[]>;
	deliveryDate?: string;
}

function calculateFinalOrderQty(
  item: BulkOrderItem,
  podQty: number | undefined,
): number {
  const applyModifiers = (base: number): number => {
    const modifiers = item.demandModifiers ?? [];
    if (modifiers.length === 0) return Math.max(0, Math.round(base));
    const totalPct = modifiers
      .filter(m => m.modifierType === DemandModifierType.PercentageUplift)
      .reduce((sum, m) => sum + m.modifierValue, 0);
    const totalOffset = modifiers
      .filter(m => m.modifierType === DemandModifierType.QuantityOffset)
      .reduce((sum, m) => sum + m.modifierValue, 0);
    return Math.max(0, Math.round((base * (1 + totalPct / 100)) + totalOffset));
  };

  if (item.baseOrderQuantity != null) {
    // When an override exists, including an explicit zero, the backend (or optimistic UI update) has already
    // applied demand modifiers and stored the final value in orderQuantity.
    // Therefore we simply display orderQuantity instead of recalculating.
    return item.orderQuantity;
  }

  if (podQty !== undefined && podQty > 0) {
    return applyModifiers(podQty);
  }

  return item.orderQuantity;
}

function formatOrderableUom(item: BulkOrderItem): string {
  if (!item.orderableUom) return '-';
  if (!item.packSize || !item.primaryUom) return item.orderableUom;
  return `${item.orderableUom} (${item.packSize}${item.primaryUom})`;
}

interface OverrideCellProps {
	item: BulkOrderItem;
	onOverrideChange: (storeNumber: string, itemId: number, itemSku: string, overrideQty: number | null) => Promise<void>;
}

const parseOverrideValue = (value: string | null | undefined): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getBulkOrderItemNumber = (item: Pick<BulkOrderItem, 'itemSku'>): string => getInventorySku({ sku: item.itemSku });

const tableComponents: TableComponents<BulkOrderItem> = {
  Table: (props: React.ComponentProps<typeof Table>) => (
    <Table
      {...props}
      size="small"
      sx={{ tableLayout: 'fixed', minWidth: 1200 }}
      aria-label="bulk order table"
    />
  ),
  TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
};

const OverrideCell: React.FC<OverrideCellProps> = memo(function OverrideCell({ item, onOverrideChange }) {
  const theme = useTheme();
  const { t } = useTranslation('bulkOrderManagement');
  const [localValue, setLocalValue] = useState<string>(() =>
    item.baseOrderQuantity != null ? String(item.baseOrderQuantity) : '',
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const originalFocusValue = useRef<string | null>(null);
  const latestLocalValue = useRef(localValue);
  const isFocused = useRef(false);
  const warningRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onOverrideChangeRef = useRef(onOverrideChange);
  const itemRef = useRef(item);
  const isSaveInFlightRef = useRef(false);
  useEffect(() => { onOverrideChangeRef.current = onOverrideChange; }, [onOverrideChange]);
  useEffect(() => { itemRef.current = item; }, [item]);

  useEffect(() => {
    if (!isFocused.current) {
      setLocalValue(item.baseOrderQuantity != null ? String(item.baseOrderQuantity) : '');
    }
  }, [item.baseOrderQuantity]);

  useEffect(() => { latestLocalValue.current = localValue; }, [localValue]);
  useEffect(() => { warningRef.current = warningMessage; }, [warningMessage]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (document.activeElement === el) e.preventDefault();
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  useEffect(() => {
    return () => {
      if (originalFocusValue.current === null) return;
      const original = parseOverrideValue(originalFocusValue.current);
      const current = parseOverrideValue(latestLocalValue.current);
      if (current === original || warningRef.current) return;
      if (isSaveInFlightRef.current) return;
      const currentItem = itemRef.current;
      isSaveInFlightRef.current = true;
      onOverrideChangeRef.current(
        currentItem.storeNumber,
        currentItem.itemId,
        currentItem.itemSku,
        current,
      )
        .catch(() => { })
        .finally(() => {
          isSaveInFlightRef.current = false;
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setLocalValue('');
      setWarningMessage(null);
      return;
    }

    let numeric = Number(raw);
    if (!Number.isFinite(numeric)) {
      return;
    }

    if (numeric < 0) numeric = 0;
    if (raw.includes('.')) numeric = Math.round(numeric);
    const value = String(numeric);
    setLocalValue(value);

    if (item.minQuantity != null && numeric < item.minQuantity && numeric > 0) {
      setWarningMessage(t('minQuantityWarning', { value: item.minQuantity }));
    } else if (item.maxQuantity != null && numeric > item.maxQuantity && numeric > 0) {
      setWarningMessage(t('maxQuantityWarning', { value: item.maxQuantity }));
    } else {
      setWarningMessage(null);
    }
  };

  const handleBlur = async () => {
    isFocused.current = false;
    const original = parseOverrideValue(originalFocusValue.current);
    const current = parseOverrideValue(localValue);

    originalFocusValue.current = null;

    if (current === original) return;
    if (warningMessage) return;
    if (isSaveInFlightRef.current) return;

    setSaveStatus('saving');
    try {
      isSaveInFlightRef.current = true;
      await onOverrideChange(item.storeNumber, item.itemId, item.itemSku, current);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      originalFocusValue.current = null;
      setSaveStatus('error');
    } finally {
      isSaveInFlightRef.current = false;
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <TextField
        size="small"
        type="number"
        value={localValue}
        placeholder="-"
        onChange={handleChange}
        onBlur={handleBlur}
        inputRef={inputRef}
        onFocus={(e) => {
          isFocused.current = true;
          if (originalFocusValue.current === null) {
            originalFocusValue.current = localValue;
          }
          e.target.select();
        }}
        sx={{
          maxWidth: 100,
          '& input': { textAlign: 'center', padding: '8.5px 14px' },
          '& .MuiOutlinedInput-root': {
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              display: 'block',
              opacity: 1,
            },
          },
        }}
        inputProps={{ min: 0, step: 1 }}
      />
      {saveStatus === 'saving' && (
        <Box sx={{ position: 'absolute', right: -5, top: -5 }}>
          <CircularProgress size={16} />
        </Box>
      )}
      {saveStatus === 'saved' && (
        <Box sx={{ position: 'absolute', right: -5, top: -5, color: theme.palette.success.main }}>
          <CloudDoneIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      {saveStatus === 'error' && (
        <Box sx={{ position: 'absolute', right: -5, top: -5, color: theme.palette.error.main }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      {saveStatus === 'idle' && warningMessage && (
        <Tooltip title={warningMessage}>
          <Box sx={{ position: 'absolute', right: -5, top: -5, color: theme.palette.warning.main }}>
            <WarningIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
});

const BulkOrderTable = memo(function BulkOrderTable({
  items,
  onOverrideChange,
  selectedStores,
  onStoreGroupSelection,
  onPromoClick,
  predictedDemands,
  deliveryDate,
}: BulkOrderTableProps) {
  const theme = useTheme();
  const { t } = useTranslation('bulkOrderManagement');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof BulkOrderItem>('storeName');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());


  const podQtyByKey = useMemo<Record<string, number>>(() => {
    if (!predictedDemands || !deliveryDate) return {};
    const map: Record<string, number> = {};
    for (const [storeNumber, predictions] of Object.entries(predictedDemands)) {
      for (const pred of predictions) {
        const predItem = pred.predictedDemandItems?.find(
          p => dayjs(p.date).format('YYYY-MM-DD') === deliveryDate,
        );
        if (predItem?.hasPrediction && predItem.quantity > 0) {
          map[`${storeNumber}:${pred.itemNumber}`] = predItem.quantity;
        }
      }
    }
    return map;
  }, [predictedDemands, deliveryDate]);

  const toggleGroup = (storeNumber: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(storeNumber)) next.delete(storeNumber);
      else next.add(storeNumber);
      return next;
    });
  };

  const handleSort = (property: keyof BulkOrderItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [items, order, orderBy]);

  const groupedData = useMemo(() => {
    const groups = new Map<string, BulkOrderItem[]>();
    sortedItems.forEach(item => {
      if (!groups.has(item.storeNumber)) groups.set(item.storeNumber, []);
			groups.get(item.storeNumber)!.push(item);
    });
    return groups;
  }, [sortedItems]);

  const groupNumbers = useMemo(() => Array.from(groupedData.keys()), [groupedData]);

  const groupCounts = useMemo(
    () => groupNumbers.map(sn => (collapsedGroups.has(sn) ? 0 : groupedData.get(sn)!.length)),
    [groupNumbers, groupedData, collapsedGroups],
  );

  const flatItems = useMemo(() => {
    const result: BulkOrderItem[] = [];
    groupNumbers.forEach(sn => {
      if (!collapsedGroups.has(sn)) result.push(...groupedData.get(sn)!);
    });
    return result;
  }, [groupNumbers, groupedData, collapsedGroups]);

  if (items.length === 0) return null;

  return (
    <Box sx={{ height: 'max(75vh, 500px)', border: '1px solid', borderColor: theme.palette.custom.gray[300] }}>
      <GroupedTableVirtuoso
        style={{ height: '100%' }}
        overscan={20}
        groupCounts={groupCounts}
        components={tableComponents}
        fixedHeaderContent={() => (
          <TableRow
            sx={{
              backgroundColor: theme.palette.custom.gray[200],
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <TableCell sx={{ fontWeight: 'bold', width: 150, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'storeName'}
                direction={orderBy === 'storeName' ? order : 'asc'}
                onClick={() => handleSort('storeName')}
              >
                {t('table.store')}
              </TableSortLabel>
            </TableCell>

            <TableCell sx={{ fontWeight: 'bold', width: 100, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'itemSku'}
                direction={orderBy === 'itemSku' ? order : 'asc'}
                onClick={() => handleSort('itemSku')}
              >
                {t('table.sku')}
              </TableSortLabel>
            </TableCell>

            <TableCell sx={{ fontWeight: 'bold', width: 220, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'itemName'}
                direction={orderBy === 'itemName' ? order : 'asc'}
                onClick={() => handleSort('itemName')}
              >
                {t('table.item')}
              </TableSortLabel>
            </TableCell>

            <TableCell sx={{ fontWeight: 'bold', width: 130, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'category'}
                direction={orderBy === 'category' ? order : 'asc'}
                onClick={() => handleSort('category')}
              >
                {t('table.category')}
              </TableSortLabel>
            </TableCell>

            <TableCell align="right"
              sx={{ fontWeight: 'bold', width: 90, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'price'}
                direction={orderBy === 'price' ? order : 'asc'}
                onClick={() => handleSort('price')}
              >
                {t('table.price')}
              </TableSortLabel>
            </TableCell>

            <TableCell
              align="center"
              sx={{ fontWeight: 'bold', width: 120, backgroundColor: theme.palette.custom.gray[200] }}
            >
              {t('uom')}
            </TableCell>

            <TableCell align="right"
              sx={{ fontWeight: 'bold', width: 90, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'stockOnHand'}
                direction={orderBy === 'stockOnHand' ? order : 'asc'}
                onClick={() => handleSort('stockOnHand')}
              >
                {t('table.soh')}
              </TableSortLabel>
            </TableCell>

            <TableCell align="right"
              sx={{ fontWeight: 'bold', width: 90, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'forecast'}
                direction={orderBy === 'forecast' ? order : 'asc'}
                onClick={() => handleSort('forecast')}
              >
                {t('table.forecast')}
              </TableSortLabel>
            </TableCell>

            <TableCell align="right"
              sx={{ fontWeight: 'bold', width: 80, backgroundColor: theme.palette.custom.gray[200] }}>
              {t('table.par')}
            </TableCell>

            <TableCell align="center"
              sx={{ fontWeight: 'bold', width: 130, backgroundColor: theme.palette.custom.gray[200] }}>
              {t('table.override')}
            </TableCell>

            <TableCell align="center"
              sx={{ fontWeight: 'bold', width: 190, backgroundColor: theme.palette.custom.gray[200] }}>
              {t('table.demandModifiers')}
            </TableCell>

            <TableCell align="center"
              sx={{ fontWeight: 'bold', width: 120, backgroundColor: theme.palette.custom.gray[200] }}>
              <TableSortLabel
                active={orderBy === 'orderQuantity'}
                direction={orderBy === 'orderQuantity' ? order : 'asc'}
                onClick={() => handleSort('orderQuantity')}
              >
                {t('table.orderQty')}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        )}
        groupContent={(groupIndex) => {
          const storeNumber = groupNumbers[groupIndex];
          const storeItems = groupedData.get(storeNumber)!;
          const firstItem = storeItems[0];
          const isCollapsed = collapsedGroups.has(storeNumber);
          const isSelected = selectedStores.has(storeNumber);

          return (
            <TableCell
              colSpan={12}
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderBottom: `1px solid ${theme.palette.divider}`,
                p: 0,
                position: 'sticky',
                top: 39,
                zIndex: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 1, pr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onStoreGroupSelection(storeNumber, e.target.checked);
                    }}
                    sx={{ ml: '12px', color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                  />
                  <SvgIcon
                    sx={{
                      fontSize: 16,
                      color: 'white',
                      cursor: 'pointer',
                      transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                      transition: 'transform 0.2s ease',
                      ml: '4px',
                    }}
                    onClick={() => toggleGroup(storeNumber)}
                  >
                    <ChevronRight />
                  </SvgIcon>
                  <Typography
                    variant="textSM"
                    fontWeight="500"
                    color="white"
                    onClick={() => toggleGroup(storeNumber)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {firstItem.storeName}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: '2px',
                    borderRadius: 1,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleGroup(storeNumber)}
                >
                  <Typography variant="textXS"
                    color="white"
                    fontWeight="500">
                    {t('table.itemsCount', { count: storeItems.length })}
                  </Typography>
                </Box>
              </Box>
            </TableCell>
          );
        }}
        itemContent={(itemIndex) => {
          const item = flatItems[itemIndex];
          if (!item) return null;
          const podKey = `${item.storeNumber}:${getBulkOrderItemNumber(item)}`;

          return (
            <>
              <TableCell sx={{ width: 150, pl: 6 }}>
                <Typography variant="textSM">{item.storeName}</Typography>
              </TableCell>

              <TableCell sx={{ width: 100 }}>
                <Typography variant="textSM">{item.itemSku}</Typography>
              </TableCell>

              <TableCell sx={{ width: 220 }}>
                <Typography variant="textSM">{item.itemName}</Typography>
              </TableCell>

              <TableCell sx={{ width: 130 }}>
                <Typography variant="textSM">{item.category}</Typography>
              </TableCell>

              <TableCell align="right"
                sx={{ width: 90 }}>
                <Typography variant="textSM">
                  {item.price != null ? `$${item.price.toFixed(2)}` : '-'}
                </Typography>
              </TableCell>

              <TableCell align="center"
                sx={{ width: 120 }}>
                <Typography variant="textSM">{formatOrderableUom(item)}</Typography>
              </TableCell>

              <TableCell align="right"
                sx={{ width: 90 }}>
                <Typography variant="textSM">{item.stockOnHand}</Typography>
              </TableCell>

              <TableCell align="right"
                sx={{ width: 90 }}>
                <Typography variant="textSM">
                  {podQtyByKey[podKey] ?? item.forecast ?? '-'}
                </Typography>
              </TableCell>

              <TableCell align="right"
                sx={{ width: 80 }}>
                <Typography variant="textSM">
                  {item.par ?? '-'}
                </Typography>
              </TableCell>

              <TableCell align="center"
                sx={{ width: 130 }}>
                <OverrideCell
                  item={item}
                  onOverrideChange={onOverrideChange}
                />
              </TableCell>

              <TableCell align="center"
                sx={{ width: 190 }}>
                {(item.demandModifiers ?? []).length === 0 ? (
                  <Tooltip title={t('table.addDemandModifierTooltip')}>
                    <Typography
                      variant="textSM"
                      onClick={() => onPromoClick(item.storeNumber, item.itemId, undefined)}
                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'text.secondary' }}
                    >
											-
                    </Typography>
                  </Tooltip>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    {(item.demandModifiers ?? []).map((mod: BulkOrderItemModifier) => {
                      const valueLabel = mod.modifierType === DemandModifierType.PercentageUplift
                        ? (mod.modifierValue >= 0 ? `+${mod.modifierValue}%` : `${mod.modifierValue}%`)
                        : (mod.modifierValue >= 0 ? `+${mod.modifierValue}` : `${mod.modifierValue}`);
                      return (
                        <Tooltip key={mod.id}
                          title={`${mod.name}: ${valueLabel}`}>
                          <Chip
                            label={mod.name}
                            size="small"
                            color="success"
                            onClick={() => onPromoClick(item.storeNumber, item.itemId, mod.id)}
                            sx={{
                              backgroundColor: theme.palette.success[50],
                              color: theme.palette.success[700],
                              border: `1px solid ${theme.palette.success[200]}`,
                              fontSize: '0.75rem',
                              height: '22px',
                              cursor: 'pointer',
                              '& .MuiChip-label': { padding: '0 8px' },
                              '&:hover': { backgroundColor: theme.palette.success[100] },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </TableCell>

              <TableCell align="center"
                sx={{ width: 120 }}>
                <Typography variant="textSM"
                  fontWeight={500}>
                  {calculateFinalOrderQty(item, podQtyByKey[podKey])}
                </Typography>
              </TableCell>
            </>
          );
        }}
      />
    </Box>
  );
});

export default BulkOrderTable;
